/**
 * Per-agent telephony — phone-number management types, mirroring
 * iceberger-back's PhoneNumberController views 1:1 (see the OpenAPI spec:
 * PhoneNumberView, AvailablePhoneNumberView, MyPhoneNumberView).
 *
 * Model notes:
 *   - `kind` is what the number IS: COMPANY (the single workspace main line,
 *     used as caller-ID fallback, never assignable to an agent) or LINE (an
 *     agent line).
 *   - `level` is who ANSWERS it right now: COMPANY, AGENT (has `user`), or
 *     UNASSIGNED (a LINE in the pool).
 *   - Released numbers are kept for history (`releasedAt`) and only returned
 *     when the list is queried with `includeReleased`.
 */

export type TPhoneNumberKind = "COMPANY" | "LINE";
export type TPhoneNumberLevel = "COMPANY" | "AGENT" | "UNASSIGNED";
export type TPhoneNumberStatus = "PROVISIONING" | "ACTIVE" | "PORT_PENDING" | "RELEASED";

export type TPhoneNumberUser = {
  id: number;
  firstName: string | null;
  lastName: string | null;
};

export type TPhoneNumber = {
  id: number;
  e164: string;
  country: string;
  locality: string | null;
  label: string | null;
  kind: TPhoneNumberKind;
  level: TPhoneNumberLevel;
  status: TPhoneNumberStatus;
  user?: TPhoneNumberUser;
  purchasedAt: string | null;
  releasedAt: string | null;
};

export type TAvailablePhoneNumber = {
  e164: string;
  locality: string | null;
  region: string | null;
};

export type TPhoneNumberSearchPayload = {
  country: string;
  areaCode: string;
};

export type TPurchasePhoneNumberPayload = {
  e164: string;
  country: string;
  locality?: string | null;
  kind: TPhoneNumberKind;
  userId?: number | null;
  label?: string | null;
};

export type TMyCallerId = {
  e164: string;
  source: "AGENT" | "COMPANY";
  phoneNumberId: number;
};

export type TMyPhoneNumber = {
  number?: TPhoneNumber;
  callerId?: TMyCallerId;
};

/**
 * Agent-facing copy for the missing-caller-ID case, shared by the pre-flight
 * gates (calls, texts) and the backend's NO_CALLER_ID rejection.
 */
export const NO_OUTBOUND_NUMBER_MESSAGE = "No outbound number is set up for you; ask an admin.";

/** MVP purchase scope — US & Canada only (backend rejects the rest). */
export const PHONE_NUMBER_COUNTRIES = [
  { code: "US", label: "United States", flag: "🇺🇸" },
  { code: "CA", label: "Canada", flag: "🇨🇦" },
] as const;

/** "+14155550137" → "(415) 555-0137". Non-NANP input is returned untouched. */
export const formatE164 = (e164: string): string => {
  const digits = e164.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10) return e164;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
};

/**
 * Backend error codes → agent-facing copy. The API signals business failures
 * through these codes in the error message (409/422/400 responses); anything
 * unrecognized falls through to getErrorFromBackend's generic handling.
 */
const PHONE_NUMBER_ERROR_COPY: Record<string, string> = {
  COMPANY_LINE_EXISTS: "Your workspace already has a company line.",
  AGENT_ALREADY_HAS_NUMBER: "That agent already has a phone number assigned.",
  NUMBER_NOT_AVAILABLE: "That number was just taken. Search again and pick another one.",
  NUMBER_NOT_FOUND: "Phone number not found. It may have been released already.",
  NUMBER_NOT_ASSIGNABLE: "This number can't be reassigned.",
  COMPANY_LINE_NOT_ASSIGNABLE: "The company line can't be assigned to an agent.",
  USER_NOT_ASSIGNABLE: "That user can't receive a phone number.",
  INVALID_E164: "That phone number isn't valid.",
  COUNTRY_NOT_SUPPORTED: "Only US and Canada numbers are supported for now.",
  AREA_CODE_INVALID: "Enter a valid 3-digit area code.",
};

export const phoneNumberErrorCopy = (error: unknown): string | null => {
  const message =
    (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data
      ?.error?.message ??
    (error instanceof Error ? error.message : "");
  const code = Object.keys(PHONE_NUMBER_ERROR_COPY).find((key) => message?.includes(key));
  return code ? PHONE_NUMBER_ERROR_COPY[code] : null;
};
